/**
 * postMessage protocol shared by the mobile preview iframe host and the
 * `apps/web` preview page.
 *
 * Source of truth: both sides import these constants/types so any drift
 * surfaces at type-check time.
 */
import type { PublicProfilePublicPage } from './public-profile';

export const PROFILE_PREVIEW_MESSAGE_TYPES = {
  /** Iframe → host: emitted once after the preview component mounts. */
  Ready: 'TABBIO_PREVIEW_READY',
  /** Host → iframe: initial document + design snapshot, sent once Ready fires. */
  Init: 'TABBIO_PREVIEW_INIT',
  /** Host → iframe: replace the current document + design (debounced live edits). */
  Update: 'TABBIO_PREVIEW_UPDATE',
  /** Iframe → host: a tracked link was clicked; host decides whether to navigate. */
  Navigate: 'TABBIO_PREVIEW_NAVIGATE',
  /** Iframe → host: preview computed a new content height (optional). */
  Resize: 'TABBIO_PREVIEW_RESIZE',
} as const;

export type ProfilePreviewMessageType =
  (typeof PROFILE_PREVIEW_MESSAGE_TYPES)[keyof typeof PROFILE_PREVIEW_MESSAGE_TYPES];

export interface ProfilePreviewPayload {
  page: PublicProfilePublicPage;
  /** Surfaces editor-only signals: e.g. a not-yet-published warning banner. */
  isPubliclyVisible?: boolean;
}

export interface ProfilePreviewReadyMessage {
  type: typeof PROFILE_PREVIEW_MESSAGE_TYPES.Ready;
}

export interface ProfilePreviewInitMessage {
  type: typeof PROFILE_PREVIEW_MESSAGE_TYPES.Init;
  payload: ProfilePreviewPayload;
}

export interface ProfilePreviewUpdateMessage {
  type: typeof PROFILE_PREVIEW_MESSAGE_TYPES.Update;
  payload: ProfilePreviewPayload;
}

export interface ProfilePreviewNavigateMessage {
  type: typeof PROFILE_PREVIEW_MESSAGE_TYPES.Navigate;
  payload: {
    href: string;
    /** Original CTA kind from `buildTrackedHref` (`cta`/`block`/`job`). */
    kind?: string;
    /** Original CTA source (`owner_website`, `contact`, `portfolio`, …). */
    source?: string;
    blockId?: string;
    blockType?: string;
    jobId?: string;
  };
}

export interface ProfilePreviewResizeMessage {
  type: typeof PROFILE_PREVIEW_MESSAGE_TYPES.Resize;
  payload: { height: number };
}

export type ProfilePreviewIncomingMessage =
  | ProfilePreviewInitMessage
  | ProfilePreviewUpdateMessage;

export type ProfilePreviewOutgoingMessage =
  | ProfilePreviewReadyMessage
  | ProfilePreviewNavigateMessage
  | ProfilePreviewResizeMessage;

export type ProfilePreviewMessage =
  | ProfilePreviewIncomingMessage
  | ProfilePreviewOutgoingMessage;

export function isProfilePreviewMessage(
  value: unknown,
): value is ProfilePreviewMessage {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as { type?: unknown };
  if (typeof candidate.type !== 'string') return false;
  return (
    Object.values(PROFILE_PREVIEW_MESSAGE_TYPES) as string[]
  ).includes(candidate.type);
}
